import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'
import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, requireAuthToken } from '@/lib/api'
import { messageKeys } from '@/lib/queryKeys'
import type { ClientMessage } from '@/features/messages/types'

export interface CallMessage extends ClientMessage {
  submission_status: 'submitted' | 'unconfirmed' | 'received'
}

export function useCallMessagesQuery(callId: string) {
  const { getToken, userId } = useAuth()
  const { organization } = useOrganization()
  return useQuery({
    queryKey: messageKeys.call(organization?.id, callId, userId),
    enabled: !!callId,
    queryFn: async ({ signal }): Promise<CallMessage[]> => {
      const token = await requireAuthToken(getToken)
      const result = await fetchJsonWithAuth<{ data: CallMessage[] }>({
        token,
        url: `${AGENT_URL}/data/messages?call_id=${encodeURIComponent(callId)}`,
        signal,
        errorMessage: 'Unable to load texts for this call',
      })
      if (!Array.isArray(result.data))
        throw new Error('Invalid call messages response')
      return result.data
    },
    staleTime: 15_000,
  })
}
